import React from "react";
import {
  dateFormatType,
  getFormattedDate,
  SkeletonLoader,
} from "@SeemplicityDev/shared";
import type { CreditPack } from "../../../AnalystRuns/types";
import {
  CREDIT_PACKS_EMPTY_MESSAGE,
  CREDIT_PACKS_ERROR_MESSAGE,
  EM_DASH,
  PURCHASE_DATE_LABEL,
} from "./constants";

interface CreditPacksListProps {
  packs: CreditPack[];
  isLoading: boolean;
  isError: boolean;
  onSelectPack?: (pack: CreditPack) => void;
}

const CreditPacksList: React.FC<CreditPacksListProps> = ({
  packs,
  isLoading,
  isError,
  onSelectPack,
}) => {
  return (
    <div className="account-configs-list">
      <div className="account-configs-list-header">
        <h4 className="account-configs-subtitle">Credit Packs</h4>
      </div>

      {isLoading ? (
        <ul className="account-configs-rows" data-testid="credit-packs-loading">
          {Array.from({ length: 4 }, (_, index) => (
            <li className="account-configs-row" key={`skeleton-${index}`}>
              <SkeletonLoader height="19px" width="100%" />
            </li>
          ))}
        </ul>
      ) : isError ? (
        <p className="account-configs-error">{CREDIT_PACKS_ERROR_MESSAGE}</p>
      ) : !packs.length ? (
        <p className="account-configs-empty">{CREDIT_PACKS_EMPTY_MESSAGE}</p>
      ) : (
        <ul className="account-configs-rows">
          {[...packs]
            .sort((a, b) => b.id - a.id)
            .map((pack, index) => (
              <li
                className="account-configs-row account-configs-row-credits account-configs-row-clickable"
                key={pack.id}
                data-testid={`credit-packs-row-${index}`}
                role="button"
                tabIndex={0}
                onClick={() => onSelectPack?.(pack)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelectPack?.(pack);
                  }
                }}
              >
                <span
                  className="account-configs-credits-cell"
                  data-testid="credit-packs-cell-credits_purchased"
                >
                  {pack.credits_purchased}
                </span>
                <span
                  className="account-configs-credits-cell"
                  data-testid="credit-packs-cell-start_date"
                >
                  {`${PURCHASE_DATE_LABEL}: ${getFormattedDate(pack.start_date, dateFormatType.SHORT)}`}
                </span>
                <span
                  className="account-configs-credits-cell"
                  data-testid="credit-packs-cell-expiry_date"
                >
                  {getFormattedDate(pack.expiry_date, dateFormatType.SHORT)}
                </span>
                <span
                  className="account-configs-credits-cell"
                  data-testid="credit-packs-cell-batch_type"
                >
                  {pack.batch_type || EM_DASH}
                </span>
                <span
                  className="account-configs-credits-cell"
                  data-testid="credit-packs-cell-notes"
                >
                  {pack.notes || EM_DASH}
                </span>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
};

export default CreditPacksList;
